import type { StageStateV1 } from '@shotstead/contracts'
import { validateStageStateV1 } from '@shotstead/contracts'

export const SHOTSTEAD_STAGE_HISTORY_STORAGE_KEY = 'shotstead.stage-history.v1'

export class StageVersionConflictError extends Error {
  readonly expected_base_version: number
  readonly actual_latest_version: number

  constructor(expectedBaseVersion: number, actualLatestVersion: number) {
    super(`Stale Stage base version ${expectedBaseVersion}; latest committed version is ${actualLatestVersion}.`)
    this.name = 'StageVersionConflictError'
    this.expected_base_version = expectedBaseVersion
    this.actual_latest_version = actualLatestVersion
  }
}

export type StageDiff = {
  from_version: number
  to_version: number
  changed_paths: string[]
  camera_only: boolean
}

function clone<T>(value: T): T {
  return structuredClone(value)
}

function validateOrThrow(stage: unknown): StageStateV1 {
  const result = validateStageStateV1(stage)
  if (!result.valid) {
    throw new Error(`Invalid StageState: ${result.errors.join('; ')}`)
  }
  return result.value
}

function diffValues(before: unknown, after: unknown, path: string, output: string[]) {
  if (Object.is(before, after)) return

  if (Array.isArray(before) && Array.isArray(after)) {
    if (before.length !== after.length) {
      output.push(path)
      return
    }
    before.forEach((item, index) => diffValues(item, after[index], `${path}[${index}]`, output))
    return
  }

  if (
    before !== null &&
    after !== null &&
    typeof before === 'object' &&
    typeof after === 'object' &&
    !Array.isArray(before) &&
    !Array.isArray(after)
  ) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)])
    for (const key of [...keys].sort()) {
      const nextPath = path ? `${path}.${key}` : key
      diffValues(
        (before as Record<string, unknown>)[key],
        (after as Record<string, unknown>)[key],
        nextPath,
        output,
      )
    }
    return
  }

  output.push(path)
}

export function diffStageStates(before: StageStateV1, after: StageStateV1): StageDiff {
  if (before.stage_id !== after.stage_id) {
    throw new Error(`Cannot diff different stages: ${before.stage_id} vs ${after.stage_id}`)
  }

  const changedPaths: string[] = []
  diffValues(before, after, '', changedPaths)

  const semanticPaths = changedPaths.filter(
    (path) => path !== 'stage_version' && path !== 'parent_stage_version',
  )
  const cameraOnly =
    semanticPaths.length > 0 && semanticPaths.every((path) => path === 'camera' || path.startsWith('camera.'))

  return {
    from_version: before.stage_version,
    to_version: after.stage_version,
    changed_paths: changedPaths,
    camera_only: cameraOnly,
  }
}

export function sameStageContinuityState(before: StageStateV1, after: StageStateV1): boolean {
  return (
    JSON.stringify(before.environment) === JSON.stringify(after.environment) &&
    JSON.stringify(before.performers) === JSON.stringify(after.performers) &&
    JSON.stringify(before.props) === JSON.stringify(after.props) &&
    JSON.stringify(before.motion_cues) === JSON.stringify(after.motion_cues) &&
    JSON.stringify(before.continuity_constraints) === JSON.stringify(after.continuity_constraints)
  )
}

export class InMemoryStageRepository {
  private readonly stageId: string
  private readonly versions = new Map<number, StageStateV1>()

  constructor(initialVersions: StageStateV1[]) {
    if (initialVersions.length === 0) {
      throw new Error('Stage repository requires at least one committed version.')
    }

    const sorted = initialVersions.map(validateOrThrow).sort((a, b) => a.stage_version - b.stage_version)
    this.stageId = sorted[0].stage_id

    for (const stage of sorted) {
      if (stage.stage_id !== this.stageId) {
        throw new Error('A Stage repository cannot contain different stage_id values.')
      }
      if (this.versions.has(stage.stage_version)) {
        throw new Error(`Duplicate Stage version ${stage.stage_version}.`)
      }
      this.versions.set(stage.stage_version, clone(stage))
    }

    const versions = this.listVersions()
    versions.forEach((stage, index) => {
      if (index === 0) return
      const previous = versions[index - 1]
      if (stage.parent_stage_version !== previous.stage_version) {
        throw new Error(
          `Stage version ${stage.stage_version} must reference previous committed version ${previous.stage_version}.`,
        )
      }
    })
  }

  getLatest(): StageStateV1 {
    const versions = [...this.versions.keys()]
    const latestVersion = Math.max(...versions)
    return clone(this.versions.get(latestVersion)!)
  }

  getVersion(version: number): StageStateV1 {
    const stage = this.versions.get(version)
    if (!stage) throw new Error(`Unknown Stage version ${version}.`)
    return clone(stage)
  }

  listVersions(): StageStateV1[] {
    return [...this.versions.values()]
      .sort((a, b) => a.stage_version - b.stage_version)
      .map(clone)
  }

  commitFromBase(baseVersion: number, workingDraft: StageStateV1): StageStateV1 {
    if (workingDraft.stage_id !== this.stageId) {
      throw new Error(`Draft belongs to ${workingDraft.stage_id}, expected ${this.stageId}.`)
    }

    const latest = this.getLatest()
    if (baseVersion !== latest.stage_version) {
      throw new StageVersionConflictError(baseVersion, latest.stage_version)
    }

    const next = validateOrThrow({
      ...clone(workingDraft),
      stage_version: latest.stage_version + 1,
      parent_stage_version: latest.stage_version,
    })

    this.versions.set(next.stage_version, clone(next))
    return clone(next)
  }
}

export function serializeStageHistory(stages: StageStateV1[]): string {
  if (stages.length === 0) throw new Error('Cannot serialize empty Stage history.')
  return JSON.stringify(stages)
}

export function deserializeStageHistory(serialized: string): StageStateV1[] {
  const parsed: unknown = JSON.parse(serialized)
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('Stage history must be a non-empty array.')
  }

  return new InMemoryStageRepository(parsed.map(validateOrThrow)).listVersions()
}
